import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ContractClient } from './contract';
import { ReadOnlyContractClient } from './contract-readonly';
import { extractCountryInfo, getFlagUrl } from './utils/countryVaultUtils';
import { iso2ToNumeric, iso3ToIso2, numericToIso2 } from './countryCodes';
import { passkeyKitService } from './services/passkeyKitService';
import KYCSubmissionModal from './components/KYCSubmissionModal';

const _EXPERT_NET = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? 'public' : 'testnet';
function ExpertLink({ id }: { id?: string }) {
  if (!id) return null;
  return (
    <a
      href={`https://stellar.expert/explorer/${_EXPERT_NET}/contract/${id}`}
      target="_blank" rel="noopener noreferrer"
      title="View on Stellar Expert"
      style={{ color: '#4a9eff', fontSize: '0.68em', textDecoration: 'none', opacity: 0.55, marginLeft: '5px', verticalAlign: 'middle' }}
    >↗</a>
  );
}

interface CountryProfileOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  countryCode: number;
  countryName: string;
  contractClient: ContractClient | null;
  walletAddress: string | null;
  mainAdminAddress: string | null;
  wallet: any; // Wallet instance for signing transactions (Freighter)
  onManageCountry?: (country: { code: number; name: string }) => void;
  onCountryAllowedChange?: (countryCode: number, allowed: boolean) => void;
  /** Called after a successful deposit so the parent can refresh wallet/account balances */
  onDepositSuccess?: () => void;
}

export const CountryProfileOverlay: React.FC<CountryProfileOverlayProps> = ({
  isOpen,
  onClose,
  countryCode,
  countryName,
  contractClient,
  walletAddress,
  mainAdminAddress,
  wallet,
  onManageCountry,
  onCountryAllowedChange,
  onDepositSuccess,
}) => {
  const [countryAdmin, setCountryAdmin] = useState<string | null>(null);
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);
  const [isAllowed, setIsAllowed] = useState(false);
  const [isLoadingPolicy, setIsLoadingPolicy] = useState(false);
  const [isMainAdmin, setIsMainAdmin] = useState(false);
  const [isCountryAdmin, setIsCountryAdmin] = useState(false);
  const readOnlyClient = useMemo(() => new ReadOnlyContractClient(), []);
  
  // Country Vault state
  const [countryIso2, setCountryIso2] = useState<string | null>(null);
  const [vaultInfo, setVaultInfo] = useState<any>(null);
  const [vaultBalance, setVaultBalance] = useState<number | null>(null);
  const [smartAccountVaultBalance, setSmartAccountVaultBalance] = useState<number | null>(null);
  const [totalVaultBalance, setTotalVaultBalance] = useState<number | null>(null);
  const [isLoadingVault, setIsLoadingVault] = useState(false);
  const [isLoadingVaultBalance, setIsLoadingVaultBalance] = useState(false);
  
  // Deposit state
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [isDepositing, setIsDepositing] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);
  const [depositStep, setDepositStep] = useState<string | null>(null);
  const [depositSuccess, setDepositSuccess] = useState<{ amount: string; hash?: string } | null>(null);
  const [showPasskeyRegisterPrompt, setShowPasskeyRegisterPrompt] = useState(false);
  const passkeyKitAvailable = !!process.env.REACT_APP_ACCOUNT_FACTORY_ID;
  const [useWalletSigning, setUseWalletSigning] = useState(!passkeyKitAvailable); // default to wallet when passkey kit not deployed
  // Which passkey smart account to use for deposits (index into passkeyAccounts)
  const [selectedPasskeyIdx, setSelectedPasskeyIdx] = useState(0);

  // Withdraw state
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawStep, setWithdrawStep] = useState<string | null>(null);
  const [withdrawSuccess, setWithdrawSuccess] = useState<{ amount: string; hash?: string } | null>(null);
  // Default to passkey when only the smart account has vault balance
  const [useWalletWithdraw, setUseWalletWithdraw] = useState(() => {
    // We'll adjust this in a useEffect once balances load; start with wallet as fallback
    return true;
  });
  
  // Transfer state
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [transferToCountry, setTransferToCountry] = useState('');
  const [availableCountries, setAvailableCountries] = useState<Array<{ iso2: string; name: string }>>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [allowedTransferCountries, setAllowedTransferCountries] = useState<string[]>([]);
  const [isLoadingTransferCountries, setIsLoadingTransferCountries] = useState(false);
  const [transferStep, setTransferStep] = useState<string | null>(null);
  const [transferSuccess, setTransferSuccess] = useState<{ hash?: string } | null>(null);
  // Which vault to transfer from (wallet or passkey smart account) — mirrors withdraw logic
  const [useWalletTransfer, setUseWalletTransfer] = useState(true);
  
  // KYC state
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [kycStatus, setKycStatus] = useState<'pending' | 'approved' | 'rejected' | null>(null);
  const [isLoadingKYC, setIsLoadingKYC] = useState(false);
  const [complianceConfig, setComplianceConfig] = useState<{
    kyc_required: boolean;
    daily_limit: number;
    monthly_limit: number;
    sanctions_screened: boolean;
    restricted: boolean;
    min_transaction_amount: number;
    max_transaction_amount: number;
  } | null>(null);
  
  // Load available countries for transfer dropdown — lazy: only fires when the
  // transfer form is first opened so we don't flood the RPC on every overlay open.
  // Result is cached per-country (availableCountries.length > 0 guard).
  useEffect(() => {
    // Don't run until the user actually opens the transfer form
    if (!showTransferForm) return;
    // Already loaded for this country — skip
    if (availableCountries.length > 0) return;

    const loadCountries = async () => {
      if (!countryIso2 || !readOnlyClient) {
        setAvailableCountries([]);
        return;
      }
      
      setIsLoadingTransferCountries(true);
      try {
        const response = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (response.ok) {
          const geojson = await response.json();
          if (geojson.features) {
            const allCountries: Array<{ iso2: string; name: string }> = geojson.features
              .map((feature: any) => {
                const iso2 = feature.properties?.ISO2 || feature.properties?.iso_a2 || feature.properties?.ISO_A2;
                const name = feature.properties?.name || feature.properties?.NAME || feature.properties?.NAME_EN || feature.properties?.name_en;
                let finalIso2 = iso2;
                if (!finalIso2) {
                  const iso3 = feature.id || feature.properties?.id || feature.properties?.ISO3 || feature.properties?.iso_a3;
                  if (iso3) finalIso2 = iso3ToIso2(typeof iso3 === 'string' ? iso3 : String(iso3));
                }
                if (finalIso2 && name) return { iso2: String(finalIso2).toUpperCase(), name: String(name) };
                return null;
              })
              .filter((c: any): c is { iso2: string; name: string } => c !== null)
              .filter((c: { iso2: string; name: string }) => c.iso2 !== countryIso2.toUpperCase())
              .sort((a: { iso2: string; name: string }, b: { iso2: string; name: string }) => a.name.localeCompare(b.name));

            // ── Batch check all candidates in parallel ────────────────────────
            // get_transfer_allowed returns true by default; false means explicitly denied.
            // Also check whether the destination vault is registered/enabled.
            const results = await Promise.allSettled(
              allCountries.map(async (country) => {
                const [transferOk, info] = await Promise.all([
                  readOnlyClient.getTransferAllowed(countryIso2.toUpperCase(), country.iso2),
                  readOnlyClient.getCountryInfo(country.iso2).catch(() => null),
                ]);
                // Only include countries that are: not explicitly denied AND have an enabled vault
                const vaultEnabled = info && (info as any).enabled === true;
                return { country, ok: transferOk && !!vaultEnabled };
              })
            );

            const filtered = results
              .filter((r): r is PromiseFulfilledResult<{ country: { iso2: string; name: string }; ok: boolean }> =>
                r.status === 'fulfilled' && r.value.ok)
              .map(r => r.value.country);

            setAvailableCountries(filtered);
            setAllowedTransferCountries(filtered.map(c => c.iso2));
          } else {
            setAvailableCountries([]);
          }
        } else {
          setAvailableCountries([]);
        }
      } catch (error) {
        setAvailableCountries([]);
      } finally {
        setIsLoadingTransferCountries(false);
      }
    };
    loadCountries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTransferForm, countryIso2, readOnlyClient]);
  const [transferAmount, setTransferAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  // fee_bps and country_admin_share_bps for the current country (from compliance policy)
  const [feeConfig, setFeeConfig] = useState<{ feeBps: number; countryAdminShareBps: number } | null>(null);
  // Full CountryPolicyV2 — used to gate deposit/withdraw/transfer buttons
  const [fullPolicy, setFullPolicy] = useState<{
    deposits_enabled: boolean;
    withdrawals_enabled: boolean;
    transfers_in_enabled: boolean;
    transfers_out_enabled: boolean;
    restricted: boolean;
    require_webauthn_withdraw: boolean;
  } | null>(null);

  // Reset transfer country cache whenever a different country overlay is opened
  useEffect(() => {
    if (!isOpen) return;
    setAvailableCountries([]);
    setAllowedTransferCountries([]);
  }, [isOpen, countryCode]);

  // Load per-country fee configuration and full V2 policy from the compliance contract
  useEffect(() => {
    if (!isOpen || !countryCode) { setFeeConfig(null); setFullPolicy(null); return; }
    readOnlyClient.getFullCountryPolicy(countryCode).then(p => {
      setFeeConfig(p && p.fee_bps > 0
        ? { feeBps: p.fee_bps, countryAdminShareBps: p.country_admin_share_bps }
        : null);
      // Store policy permissions (null = no policy set yet → treat as all-open for backward compat)
      setFullPolicy(p ? {
        deposits_enabled: p.deposits_enabled,
        withdrawals_enabled: p.withdrawals_enabled,
        transfers_in_enabled: p.transfers_in_enabled,
        transfers_out_enabled: p.transfers_out_enabled,
        restricted: p.restricted,
        require_webauthn_withdraw: p.require_webauthn_withdraw,
      } : null);
    }).catch(() => { setFeeConfig(null); setFullPolicy(null); });
  }, [isOpen, countryCode, readOnlyClient]);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!contractClient || !walletAddress) {
        setIsMainAdmin(false);
        setIsCountryAdmin(false);
        return;
      }
      try {
        // Check if main admin
        const isMain = !!(mainAdminAddress && walletAddress === mainAdminAddress);
        setIsMainAdmin(isMain);

        // Check if country admin (use read-only client to avoid Freighter prompts)
        if (!isMain) {
          try {
            const admin = await readOnlyClient.getCountryAdmin(countryCode);
            setCountryAdmin(admin);
            setIsCountryAdmin(admin === walletAddress);
          } catch (error) {
            setCountryAdmin(null);
            setIsCountryAdmin(false);
          }
        } else {
          // Load country admin for display (use read-only client)
          try {
            const admin = await readOnlyClient.getCountryAdmin(countryCode);
            setCountryAdmin(admin);
          } catch (error) {
            setCountryAdmin(null);
          }
          setIsCountryAdmin(false); // Main admin doesn't need country admin flag
        }
      } finally {
        setIsLoadingAdmin(false);
      }
    };

    if (isOpen) {
      checkAdminStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, countryCode, contractClient, walletAddress, mainAdminAddress]);

  // Load country policy
  useEffect(() => {
    const loadCountryPolicy = async () => {
      if (!contractClient) {
        setIsLoadingPolicy(false);
        return;
      }

      setIsLoadingPolicy(true);
      try {
        const allowed = await readOnlyClient.getCountryAllowed(countryCode);
        setIsAllowed(allowed ?? false);
      } catch (error) {
        setIsAllowed(false);
      } finally {
        setIsLoadingPolicy(false);
      }
    };

    if (isOpen) {
      loadCountryPolicy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, countryCode, contractClient]);
  
  // Load country ISO2 code and vault info
  // Define loadCountryVaultInfo outside useEffect so it can be called after deposit
  const loadCountryVaultInfo = useCallback(async () => {
      // Allow loading without contractClient - we can use read-only client for vault info
      if (!isOpen) {
        return;
      }
      
      try {
        // Load GeoJSON to extract ISO2 code
        const response = await fetch('/api/countries', { headers: { 'Cache-Control': 'no-cache' } });
        if (response.ok) {
          const geojson = await response.json();
          const countryFeature = geojson.features.find((f: any) => {
            let code = f.properties?.ISO_NUMERIC;
            if (code && Number(code) === countryCode) {
              return true;
            }
            const iso2 = f.properties?.ISO2;
            if (iso2) {
              code = iso2ToNumeric(iso2);
              if (code === countryCode) return true;
            }
            const iso3 = f.id || f.properties?.id;
            if (iso3) {
              const iso3Str = typeof iso3 === 'string' ? iso3 : String(iso3);
              const iso2FromIso3 = iso3ToIso2(iso3Str);
              if (iso2FromIso3) {
                code = iso2ToNumeric(iso2FromIso3);
                if (code === countryCode) return true;
              }
            }
            return false;
          });
          
          if (countryFeature) {
            const countryInfo = extractCountryInfo(countryFeature);
            if (countryInfo && countryInfo.iso2) {
              setCountryIso2(countryInfo.iso2);
              
              // Load vault info (use read-only client to avoid wallet prompts)
              setIsLoadingVault(true);
              try {
                // Use read-only client for getCountryInfo to avoid wallet prompts
                // Ensure country code is uppercase (contract stores in uppercase)
                const info = await readOnlyClient.getCountryInfo(countryInfo.iso2.toUpperCase());
                setVaultInfo(info);
              } catch (error) {
                setVaultInfo(null);
              } finally {
                setIsLoadingVault(false);
              }
              
              // Load balances (total vault balance works without wallet, user balance requires wallet)
              if (countryInfo.iso2) {
                setIsLoadingVaultBalance(true);
                try {
                  // Use SAC (Stellar Asset Contract) for native XLM
                  // Testnet SAC: CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC
                  const assetAddress = process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS || 
                    'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';
                  const contractId = process.env.REACT_APP_V2_VAULT_ID;
                  // Use relative path in production, or env var if set
                  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                  
                  if (contractId) {
                    // Always load total vault balance (works without wallet)
                    try {
                      const totalResponse = await fetch(`${backendUrl}/api/smart-wallet/get-total-vault-balance`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          contractId: contractId,
                          countryCode: countryInfo.iso2,
                          assetAddress: assetAddress,
                          rpcUrl: process.env.REACT_APP_SOROBAN_RPC_URL
                        })
                      });
                      
                      if (!totalResponse.ok) {
                        setTotalVaultBalance(null);
                      } else {
                        const totalData = await totalResponse.json();
                        if (totalData.success) {
                          setTotalVaultBalance(Number(totalData.balance) || 0);
                        } else {
                          setTotalVaultBalance(null);
                        }
                      }
                    } catch (error) {
                      setTotalVaultBalance(null);
                    }
                    
                    // Load user's vault balance (wallet + linked passkey smart account)
                    if (walletAddress) {
                      const balanceUrl = `${backendUrl}/api/smart-wallet/get-balance`;
                      const linkedSmartAccount = passkeyKitService.getAccountByWallet(walletAddress);

                      const fetchBal = (addr: string) =>
                        fetch(balanceUrl, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            contractId,
                            userAddress: addr,
                            countryCode: countryInfo.iso2,
                            assetAddress,
                            rpcUrl: process.env.REACT_APP_SOROBAN_RPC_URL,
                          }),
                        }).then(r => r.json()).catch(() => ({ success: false, balance: 0 }));

                      try {
                        const fetches: Promise<any>[] = [fetchBal(walletAddress)];
                        if (linkedSmartAccount) fetches.push(fetchBal(linkedSmartAccount.accountAddress));

                        const [walletData, smartData] = await Promise.all(fetches);
                        // Always coerce to Number; the API may return balance as a string
                        setVaultBalance(walletData.success ? (Number(walletData.balance) || 0) : null);
                        setSmartAccountVaultBalance(linkedSmartAccount
                          ? (smartData?.success ? (Number(smartData.balance) || 0) : 0)
                          : null);
                      } catch (error) {
                        setVaultBalance(null);
                        setSmartAccountVaultBalance(null);
                      }
                      
                      // Load compliance config and KYC status
                      setIsLoadingKYC(true);
                      try {
                        const compliance = await readOnlyClient.getComplianceConfig(countryInfo.iso2);
                        setComplianceConfig(compliance);
                        
                        if (compliance && compliance.kyc_required) {
                          const hasApproved = await readOnlyClient.hasApprovedKYC(walletAddress, countryInfo.iso2);
                          if (hasApproved) {
                            setKycStatus('approved');
                          } else {
                            // Check if KYC data exists
                            const kycData = await readOnlyClient.getKYCData(walletAddress, countryInfo.iso2);
                            if (kycData) {
                              setKycStatus(kycData.kycStatus as 'pending' | 'approved' | 'rejected');
                            } else {
                              setKycStatus(null);
                            }
                          }
                        } else {
                          setKycStatus(null);
                        }
                      } catch (error) {
                      } finally {
                        setIsLoadingKYC(false);
                      }
                    } else {
                      // No wallet connected, clear user balance
                      setVaultBalance(null);
                    }
                  }
                } catch (error) {
                } finally {
                  setIsLoadingVaultBalance(false);
                }
              }
            } else {
              // Fallback: try to convert numeric code directly to ISO2
              const iso2FromNumeric = numericToIso2(countryCode);
              if (iso2FromNumeric) {
                setCountryIso2(iso2FromNumeric);
                // Try to load vault info with converted ISO2
                setIsLoadingVault(true);
                try {
                  const info = await readOnlyClient.getCountryInfo(iso2FromNumeric.toUpperCase());
                  setVaultInfo(info);
                } catch (error) {
                  setVaultInfo(null);
                } finally {
                  setIsLoadingVault(false);
                }
              } else {
                setCountryIso2(null);
              }
            }
          } else {
            // Fallback: try to convert numeric code directly to ISO2 if GeoJSON lookup failed
            const iso2FromNumeric = numericToIso2(countryCode);
            if (iso2FromNumeric) {
              setCountryIso2(iso2FromNumeric);
              // Try to load vault info with converted ISO2
              setIsLoadingVault(true);
              try {
                const info = await readOnlyClient.getCountryInfo(iso2FromNumeric.toUpperCase());
                setVaultInfo(info);
              } catch (error) {
                setVaultInfo(null);
              } finally {
                setIsLoadingVault(false);
              }
            } else {
              setCountryIso2(null);
            }
          }
        }
      } catch (error) {
        // Last resort fallback: try to convert numeric code directly to ISO2
        const iso2FromNumeric = numericToIso2(countryCode);
        if (iso2FromNumeric) {
          setCountryIso2(iso2FromNumeric);
          setIsLoadingVault(true);
          try {
            const info = await readOnlyClient.getCountryInfo(iso2FromNumeric.toUpperCase());
            setVaultInfo(info);
          } catch (error) {
            setVaultInfo(null);
          } finally {
            setIsLoadingVault(false);
          }
        }
      }
    }, [isOpen, countryCode, walletAddress, readOnlyClient]);
  
  // Call loadCountryVaultInfo when component opens or dependencies change
  useEffect(() => {
    if (isOpen) {
      loadCountryVaultInfo();
    }
  }, [isOpen, countryCode, walletAddress, readOnlyClient, loadCountryVaultInfo]);

  // Auto-select the withdraw source that actually has balance:
  // • If only passkey has balance → use passkey path
  // • If only wallet has balance → use wallet path
  // • If both have balance → keep current selection (default stays wallet)
  useEffect(() => {
    const walletBal  = vaultBalance ?? 0;
    const passKeyBal = smartAccountVaultBalance ?? 0;
    if (passKeyBal > 0 && walletBal === 0) {
      setUseWalletWithdraw(false);
    } else if (walletBal > 0 && passKeyBal === 0) {
      setUseWalletWithdraw(true);
    }
    // When both > 0, leave the user's current choice unchanged
  }, [vaultBalance, smartAccountVaultBalance]);

  // Auto-select transfer source (wallet vs passkey) based on which has balance
  useEffect(() => {
    const walletBal   = vaultBalance ?? 0;
    const passKeyBal  = smartAccountVaultBalance ?? 0;
    if (passKeyBal > 0 && walletBal === 0) {
      setUseWalletTransfer(false);
    } else if (walletBal > 0 && passKeyBal === 0) {
      setUseWalletTransfer(true);
    }
  }, [vaultBalance, smartAccountVaultBalance]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90vw',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            {countryIso2 && (
              <img
                src={getFlagUrl(countryIso2)}
                alt={countryIso2}
                style={{ width: '28px', height: 'auto', borderRadius: '3px', flexShrink: 0 }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {countryName} (Code: {countryCode})
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0 8px',
            }}
          >
            ×
          </button>
        </div>

        {/* Country Status */}
        <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Country Status</h3>
          {isLoadingPolicy ? (
            <div>Loading policy...</div>
          ) : (
            <div>
              <strong>Status:</strong>{' '}
              <span style={{ color: isAllowed ? '#4caf50' : '#d32f2f' }}>
                {isAllowed ? 'Allowed' : 'Denied'}
              </span>
            </div>
          )}
        </div>

        {/* Country Admin */}
        <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>Country Admin</h3>
          {isLoadingAdmin ? (
            <div>Loading admin...</div>
          ) : countryAdmin ? (
            <div>
              <div style={{ fontSize: '14px', wordBreak: 'break-all' }}>
                {countryAdmin}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '14px', color: '#666' }}>
              No country-specific admin assigned. Main admin manages this country.
            </div>
          )}
        </div>

        {/* Country Vault */}
        {countryIso2 && (
          <div style={{ padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #ddd' }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {countryIso2 && (
                <img
                  src={getFlagUrl(countryIso2)}
                  alt={countryIso2}
                  style={{ width: '22px', height: 'auto', borderRadius: '2px', flexShrink: 0 }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              )}
              Country Vault ({countryIso2})
              <ExpertLink id={process.env.REACT_APP_V2_VAULT_ID} />
            </h3>
            {isLoadingVault ? (
              <div>Loading vault info...</div>
            ) : vaultInfo ? (
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Registered:</strong>{' '}
                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>Yes</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Vault Status:</strong>{' '}
                  <span style={{
                    color: vaultInfo.enabled ? '#28a745' : '#dc3545',
                    fontWeight: 'bold'
                  }}>
                    {vaultInfo.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {countryName && (
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Country: {countryName}
                  </div>
                )}
                {walletAddress && (
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ddd' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '6px' }}>
                      Your Vault Balance:
                    </div>
                    {isLoadingVaultBalance ? (
                      <div style={{ fontSize: '16px', color: '#666' }}>Loading...</div>
                    ) : (() => {
                      const walletBal = vaultBalance ?? 0;
                      const smartBal  = smartAccountVaultBalance ?? 0;
                      const combined  = walletBal + smartBal;
                      const hasPasskey = smartAccountVaultBalance !== null;
                      return combined > 0 || hasPasskey ? (
                        <div>
                          <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700', marginBottom: hasPasskey ? '4px' : 0 }}>
                            {(combined / 10_000_000).toFixed(7)} XLM
                          </div>
                          {hasPasskey && (
                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px', lineHeight: '1.6' }}>
                              {walletBal > 0 && <span>Wallet: {(walletBal / 10_000_000).toFixed(2)} XLM{smartBal > 0 ? '  ·  ' : ''}</span>}
                              {smartBal > 0 && <span>Passkey: {(smartBal / 10_000_000).toFixed(2)} XLM</span>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '14px', color: '#888' }}>No balance</div>
                      );
                    })()}
                    
                    {vaultInfo.enabled && (
                      <div style={{ marginTop: '12px' }}>
                        {/* KYC Status Badge */}
                        {complianceConfig && complianceConfig.kyc_required && walletAddress && (
                          <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
                              KYC Status:
                              <ExpertLink id={process.env.REACT_APP_V2_COMPLIANCE_ID} />
                            </div>
                            {isLoadingKYC ? (
                              <div style={{ fontSize: '12px', color: '#666' }}>Loading...</div>
                            ) : kycStatus === 'approved' ? (
                              <div style={{ fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>✓ Approved</div>
                            ) : kycStatus === 'pending' ? (
                              <div style={{ fontSize: '12px', color: '#ffc107', fontWeight: 'bold' }}>⏳ Pending Review</div>
                            ) : kycStatus === 'rejected' ? (
                              <div style={{ fontSize: '12px', color: '#dc3545', fontWeight: 'bold' }}>✗ Rejected</div>
                            ) : (
                              <div style={{ fontSize: '12px', color: '#dc3545', fontWeight: 'bold' }}>⚠ Not Submitted</div>
                            )}
                            {kycStatus !== 'approved' && (
                              <button
                                onClick={() => setShowKYCModal(true)}
                                style={{
                                  marginTop: '8px',
                                  padding: '6px 12px',
                                  backgroundColor: '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                {kycStatus === null ? 'Submit KYC' : 'Update KYC'}
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Policy-restricted banner */}
                        {fullPolicy?.restricted && (
                          <div style={{ marginBottom: '10px', padding: '8px 12px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', fontSize: '12px', color: '#856404' }}>
                            ⚠️ Transactions are currently restricted for this country by policy.
                          </div>
                        )}

                        {!showDepositForm && !showTransferForm && !showWithdrawForm ? (
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                            {/* Deposit — gated by policy deposits_enabled */}
                            {(fullPolicy === null || fullPolicy.deposits_enabled) && !fullPolicy?.restricted && (
                              <button
                                onClick={() => {
                                  if (complianceConfig && complianceConfig.kyc_required && kycStatus !== 'approved') {
                                    setShowKYCModal(true);
                                    return;
                                  }
                                  setDepositSuccess(null);
                                  setDepositError(null);
                                  setDepositStep(null);
                                  setShowPasskeyRegisterPrompt(false);
                                  setShowDepositForm(true);
                                  setShowTransferForm(false);
                                  setShowWithdrawForm(false);
                                }}
                                disabled={!!(complianceConfig && complianceConfig.kyc_required && kycStatus !== 'approved')}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: !!(complianceConfig && complianceConfig.kyc_required && kycStatus !== 'approved') ? '#ccc' : '#28a745',
                                  color: 'white', border: 'none', borderRadius: '4px',
                                  cursor: !!(complianceConfig && complianceConfig.kyc_required && kycStatus !== 'approved') ? 'not-allowed' : 'pointer',
                                  fontSize: '14px', fontWeight: 'bold'
                                }}
                              >
                                Deposit
                              </button>
                            )}
                            {/* Transfer — gated by transfers_in_enabled (receiving) */}
                            {((vaultBalance ?? 0) + (smartAccountVaultBalance ?? 0)) > 0 &&
                              (fullPolicy === null || fullPolicy.transfers_out_enabled) && !fullPolicy?.restricted && (
                              <button
                                onClick={() => { setShowTransferForm(true); setShowDepositForm(false); setShowWithdrawForm(false); }}
                                style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                              >
                                Transfer
                              </button>
                            )}
                            {/* Withdraw — gated by withdrawals_enabled */}
                            {((vaultBalance ?? 0) + (smartAccountVaultBalance ?? 0)) > 0 &&
                              (fullPolicy === null || fullPolicy.withdrawals_enabled) && !fullPolicy?.restricted && (
                              <button
                                onClick={() => {
                                  setWithdrawSuccess(null);
                                  setWithdrawError(null);
                                  setWithdrawStep(null);
                                  setWithdrawAmount('');
                                  setShowWithdrawForm(true);
                                  setShowDepositForm(false);
                                  setShowTransferForm(false);
                                }}
                                style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                              >
                                Withdraw
                              </button>
                            )}
                          </div>
                        ) : null}
                        {showDepositForm ? (
                          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              Deposit to {countryIso2} Vault
                              <ExpertLink id={process.env.REACT_APP_V2_VAULT_ID} />
                            </h4>
                            {/* Balance summary */}
                            <div style={{ marginBottom: '12px', padding: '8px 10px', backgroundColor: '#f8f8f8', borderRadius: '6px', fontSize: '12px' }}>
                              {(() => {
                                const walletBal = vaultBalance ?? 0;
                                const smartBal  = smartAccountVaultBalance ?? 0;
                                const combined  = walletBal + smartBal;
                                const hasPasskey = smartAccountVaultBalance !== null;
                                return (
                                  <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: hasPasskey ? '2px' : 0 }}>
                                      <span style={{ color: '#555' }}>Your balance:</span>
                                      <span style={{ fontWeight: 600, color: '#222' }}>{(combined / 10_000_000).toFixed(2)} XLM</span>
                                    </div>
                                    {hasPasskey && walletBal > 0 && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888' }}>
                                        <span>↳ Wallet</span><span>{(walletBal / 10_000_000).toFixed(2)} XLM</span>
                                      </div>
                                    )}
                                    {hasPasskey && smartBal > 0 && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888' }}>
                                        <span>↳ Passkey</span><span>{(smartBal / 10_000_000).toFixed(2)} XLM</span>
                                      </div>
                                    )}
                                    {totalVaultBalance !== null && (
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e8e8e8' }}>
                                        <span style={{ color: '#4CAF50' }}>Vault total (all users):</span>
                                        <span style={{ fontWeight: 600, color: '#4CAF50' }}>{(totalVaultBalance / 10_000_000).toFixed(2)} XLM</span>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                                Amount (XLM):
                              </label>
                              <input
                                type="number"
                                step="0.0000001"
                                min="0"
                                value={depositAmount}
                                onChange={(e) => {
                                  setDepositAmount(e.target.value);
                                  setDepositError(null);
                                }}
                                placeholder="10.5"
                                disabled={isDepositing}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontSize: '14px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                            </div>
                            {/* Fee preview — shown whenever the country has a non-zero fee_bps */}
                            {feeConfig && depositAmount && parseFloat(depositAmount) > 0 && (
                              <div style={{
                                padding: '8px 10px',
                                backgroundColor: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: '4px',
                                marginBottom: '10px',
                                fontSize: '12px',
                                color: '#7c5400',
                              }}>
                                <div style={{ fontWeight: 600, marginBottom: '3px' }}>Fee breakdown</div>
                                <div>Fee ({(feeConfig.feeBps / 100).toFixed(2)}%): {(parseFloat(depositAmount) * feeConfig.feeBps / 10000).toFixed(7)} XLM</div>
                                <div>Credited to your vault: <strong>{(parseFloat(depositAmount) * (1 - feeConfig.feeBps / 10000)).toFixed(7)} XLM</strong></div>
                                <div style={{ marginTop: '3px', fontSize: '11px', color: '#9c6e00' }}>
                                  {parseFloat((feeConfig.countryAdminShareBps / 100).toFixed(2))}% of fee → country admin · {parseFloat(((10000 - feeConfig.countryAdminShareBps) / 100).toFixed(2))}% → protocol
                                </div>
                              </div>
                            )}
                            {depositError && (
                              <div style={{ 
                                padding: '8px', 
                                backgroundColor: '#fee', 
                                color: '#c33', 
                                borderRadius: '4px', 
                                marginBottom: '12px',
                                fontSize: '12px'
                              }}>
                                {depositError}
                              </div>
                            )}
                            {/* Deposit Method Selection — only show when both wallet and passkey kit are available */}
                            {wallet && passkeyKitAvailable && (
                              <div style={{ marginBottom: '14px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                  Auth Method
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {[
                                    { label: '🔑 Passkey', value: false },
                                    { label: '👛 Wallet', value: true },
                                  ].map(({ label, value }) => {
                                    const active = useWalletSigning === value;
                                    return (
                                      <button
                                        key={String(value)}
                                        onClick={() => !isDepositing && setUseWalletSigning(value)}
                                        disabled={isDepositing}
                                        style={{
                                          flex: 1,
                                          padding: '7px 10px',
                                          fontSize: '12px',
                                          fontWeight: active ? 600 : 400,
                                          borderRadius: '8px',
                                          border: active ? '1.5px solid #4a9eff' : '1.5px solid rgba(255,255,255,0.15)',
                                          background: active ? 'rgba(74,158,255,0.18)' : 'rgba(255,255,255,0.06)',
                                          color: active ? '#4a9eff' : '#aaa',
                                          cursor: isDepositing ? 'not-allowed' : 'pointer',
                                          transition: 'all 0.15s ease',
                                          outline: 'none',
                                        }}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                                {/* Passkey account selector — shown when passkey mode is active and multiple accounts exist */}
                                {!useWalletSigning && (() => {
                                  const allPasskeyAccounts = passkeyKitService.getAccounts().filter(a => a.walletAddress === walletAddress);
                                  if (allPasskeyAccounts.length <= 1) return null;
                                  return (
                                    <div style={{ marginTop: '8px' }}>
                                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Smart Account</div>
                                      <select
                                        value={selectedPasskeyIdx}
                                        onChange={e => setSelectedPasskeyIdx(Number(e.target.value))}
                                        disabled={isDepositing}
                                        style={{ width: '100%', padding: '6px 8px', fontSize: '12px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', color: '#333' }}
                                      >
                                        {allPasskeyAccounts.map((acc, idx) => (
                                          <option key={acc.credentialId} value={idx}>
                                            {acc.accountAddress.slice(0, 8)}…{acc.accountAddress.slice(-6)}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={async () => {
                                  if (!depositAmount || parseFloat(depositAmount) <= 0) {
                                    setDepositError('Please enter a valid amount');
                                    return;
                                  }
                                  
                                  if (!walletAddress || !countryIso2) {
                                    setDepositError('Wallet or country code missing');
                                    return;
                                  }
                                  
                                  setIsDepositing(true);
                                  setDepositError(null);
                                  setDepositStep(null);
                                  setDepositSuccess(null);
                                  
                                  try {
                                    // First, verify the country vault is registered and enabled
                                    if (!vaultInfo) {
                                      throw new Error('Country vault is not registered. Please register it first via the admin panel.');
                                    }
                                    
                                    if (!vaultInfo.enabled) {
                                      throw new Error('Country vault is disabled. Please enable it first via the admin panel.');
                                    }
                                    
                                    // Convert amount to stroops
                                    const amountInStroops = Math.round(parseFloat(depositAmount) * 10000000);
                                    
                                    // Get asset address - use SAC (Stellar Asset Contract) for native XLM
                                    const assetAddress = process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS || 
                                      'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';
                                    
                                    const countryCodeUpper = countryIso2.toUpperCase();
                                    
                                    // Get backend URL
                                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                    
                                    if (useWalletSigning) {
                                      // WALLET SIGNING METHOD (Freighter)
                                      if (!contractClient || !wallet) {
                                        throw new Error('Contract client or wallet not available. Please connect your wallet.');
                                      }
                                      setDepositStep('Building transaction...');
                                      
                                      // Build transaction using contractClient, sign with Freighter, send signed XDR to backend
                                      // We need to build the transaction manually because contractClient.deposit() requires WebAuthn
                                      // Import required SDK components
                                      const { Contract, rpc, Networks, TransactionBuilder, BASE_FEE, Address, xdr } = await import('@stellar/stellar-sdk');
                                      
                                      // Get RPC server
                                      const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
                                      const sorobanServer = new rpc.Server(rpcUrl);
                                      
                                      // Get network passphrase
                                      const networkPassphrase = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' 
                                        ? Networks.PUBLIC 
                                        : Networks.TESTNET;
                                      
                                      // Get user's account
                                      const publicKey = await wallet.getPublicKey();
                                      const account = await sorobanServer.getAccount(publicKey);
                                      
                                      // Create V2 vault contract instance
                                      const contractId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!contractId) {
                                        throw new Error('V2 vault contract ID not configured (REACT_APP_V2_VAULT_ID)');
                                      }
                                      const contract = new Contract(contractId);

                                      // Build ScVal arguments for V2 deposit:
                                      // deposit(user, country: u32, asset, amount, webauthn_sig: Option<WebAuthnSigData>, sig_payload: Option<Bytes>)
                                      const userScVal = Address.fromString(walletAddress).toScVal();

                                      // V2: country is u32 numeric (ISO 3166-1), not BytesN<2>
                                      // Use the full iso2ToNumeric lookup (covers all ~250 countries)
                                      const numericCountry = iso2ToNumeric(countryCodeUpper) || 0;
                                      const countryCodeScVal = xdr.ScVal.scvU32(numericCountry);

                                      const assetScVal = Address.fromString(assetAddress).toScVal();

                                      // Convert amount to i128 ScVal
                                      const amountBigInt = BigInt(amountInStroops);
                                      const hi = amountBigInt >> BigInt(64);
                                      const lo = amountBigInt & BigInt('0xFFFFFFFFFFFFFFFF');
                                      const amountI128 = new xdr.Int128Parts({
                                        hi: xdr.Int64.fromString(hi.toString()),
                                        lo: xdr.Uint64.fromString(lo.toString())
                                      });
                                      const amountScVal = xdr.ScVal.scvI128(amountI128);

                                      // V2: no WebAuthn for wallet-signed deposits — pass void for both optional args
                                      const webauthnSigDataScVal = xdr.ScVal.scvVoid();
                                      const sigPayloadScVal = xdr.ScVal.scvVoid();

                                      // Build V2 deposit transaction
                                      const contractOp = contract.call(
                                        'deposit',
                                        userScVal,
                                        countryCodeScVal,
                                        assetScVal,
                                        amountScVal,
                                        webauthnSigDataScVal,
                                        sigPayloadScVal
                                      );
                                      const transaction = new TransactionBuilder(account, {
                                        fee: BASE_FEE,
                                        networkPassphrase: networkPassphrase,
                                      })
                                        .addOperation(contractOp)
                                        .setTimeout(30)
                                        .build();
                                      
                                      // Simulate and prepare the transaction BEFORE signing
                                      // The contract is now fixed, so simulation should work correctly
                                      // This is required: Freighter must sign the PREPARED transaction
                                      // (prepared tx has resource limits set; signing unprepared tx won't work)
                                      const simulation = await sorobanServer.simulateTransaction(transaction);
                                      if ('error' in simulation && simulation.error) {
                                        throw new Error(`Simulation failed: ${JSON.stringify(simulation.error)}`);
                                      }
                                      const preparedTx = await sorobanServer.prepareTransaction(transaction);
                                      setDepositStep('Open Freighter to sign...');
                                      
                                      // Sign the PREPARED transaction (resource limits already set)
                                      const xdrString = preparedTx.toXDR();
                                      const signedXdr = await wallet.signTransaction(xdrString, networkPassphrase);
                                      setDepositStep('Submitting to blockchain...');
                                      
                                      // Send signed XDR to backend
                                      const depositResponse = await fetch(`${backendUrl}/api/smart-wallet/deposit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          contractId: contractId,
                                          userAddress: walletAddress,
                                          countryCode: countryCodeUpper,
                                          assetAddress: assetAddress,
                                          amount: amountInStroops,
                                          signedXDR: signedXdr, // Send signed XDR instead of WebAuthn data
                                          networkPassphrase: networkPassphrase,
                                          rpcUrl: rpcUrl
                                        })
                                      });
                                      
                                      if (!depositResponse.ok) {
                                        const errorText = await depositResponse.text();
                                        let errorData;
                                        try {
                                          errorData = JSON.parse(errorText);
                                        } catch {
                                          errorData = { error: errorText || 'Deposit failed', message: depositResponse.statusText };
                                        }
                                        throw new Error(errorData.message || errorData.error || `Deposit failed (${depositResponse.status})`);
                                      }
                                      
                                      const depositData = await depositResponse.json();
                                      if (!depositData.success) {
                                        throw new Error(depositData.message || depositData.error || 'Deposit failed');
                                      }
                                      
                                      setDepositSuccess({ amount: depositAmount, hash: depositData.hash });
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    } else {
                                      // PASSKEY KIT METHOD — true passkey-only auth via SmartAccount.__check_auth
                                      //
                                      // The user's Stellar address IS their SmartAccount contract.
                                      // __check_auth verifies the P-256/WebAuthn signature on-chain.
                                      // No Freighter wallet popup is needed.

                                      const vaultId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!vaultId) throw new Error('REACT_APP_V2_VAULT_ID not configured');

                                      const allPasskeyAccounts = passkeyKitService.getAccounts().filter(a => a.walletAddress === walletAddress);
                                      let smartAccount = allPasskeyAccounts[selectedPasskeyIdx] ?? allPasskeyAccounts[0] ?? null;
                                      if (!smartAccount) {
                                        setIsDepositing(false);
                                        setShowPasskeyRegisterPrompt(true);
                                        return;
                                      }

                                      const numericCountryPK = iso2ToNumeric(countryCodeUpper) || countryCode;

                                      setDepositStep('Touch your fingerprint or Face ID...');
                                      const signedXdrPK = await passkeyKitService.buildAndSignDeposit(
                                        smartAccount,
                                        numericCountryPK,
                                        assetAddress,
                                        amountInStroops,
                                        vaultId,
                                      );

                                      setDepositStep('Submitting to blockchain...');
                                      // Backend adds the service-account fee signature and submits
                                      const pkResp = await fetch(`${backendUrl}/api/passkey-kit/fee-pay-and-submit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ xdrWithPasskeySig: signedXdrPK }),
                                      });

                                      if (!pkResp.ok) {
                                        const errData = await pkResp.json().catch(() => ({ error: pkResp.statusText }));
                                        throw new Error(errData.error || `Submit failed (${pkResp.status})`);
                                      }

                                      const pkData = await pkResp.json();
                                      if (!pkData.success) throw new Error(pkData.error || 'Submit failed');

                                      setDepositSuccess({ amount: depositAmount, hash: pkData.hash });
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    }
                                  } catch (error: any) {
                                    setDepositError(error.message || 'Deposit failed. Please try again.');
                                    setDepositStep(null);
                                  } finally {
                                    setIsDepositing(false);
                                  }
                                }}
                                id="deposit-confirm-btn"
                                disabled={isDepositing || !depositAmount || !!depositSuccess}
                                style={{
                                  flex: 1,
                                  padding: '8px 16px',
                                  backgroundColor: (isDepositing || !!depositSuccess) ? '#ccc' : '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: (isDepositing || !!depositSuccess) ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {isDepositing ? 'Depositing...' : 'Confirm Deposit'}
                              </button>
                              <button
                                onClick={() => {
                                  setShowDepositForm(false);
                                  setDepositAmount('');
                                  setDepositError(null);
                                  setDepositStep(null);
                                  setDepositSuccess(null);
                                  setShowPasskeyRegisterPrompt(false);
                                }}
                                disabled={isDepositing}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#666',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: isDepositing ? 'not-allowed' : 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                Cancel
                              </button>
                            </div>

                            {/* ── Passkey register prompt ────────────────────── */}
                            {showPasskeyRegisterPrompt && !isDepositing && !depositSuccess && (
                              <div style={{ marginTop: '14px', padding: '14px', backgroundColor: '#fffbe6', border: '1px solid #ffe58f', borderRadius: '8px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: '#7c4a00' }}>No Passkey Account found</div>
                                <div style={{ fontSize: '12px', color: '#5a3800', marginBottom: '12px', lineHeight: '1.5' }}>
                                  Register a passkey now so you can deposit without Freighter. Your device will ask for a fingerprint, Face ID, or security key.
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <button
                                    onClick={async () => {
                                      setShowPasskeyRegisterPrompt(false);
                                      setIsDepositing(true);
                                      setDepositStep('Registering passkey...');
                                      try {
                                        await passkeyKitService.register(
                                          `GeoTrust ${(walletAddress || '').slice(0, 8)}`,
                                          walletAddress || '',
                                        );
                                        setDepositStep(null);
                                        setIsDepositing(false);
                                        // Trigger deposit again now that the account exists
                                        (document.getElementById('deposit-confirm-btn') as HTMLButtonElement | null)?.click();
                                      } catch (regErr: any) {
                                        setDepositError(regErr.message || 'Registration failed');
                                        setDepositStep(null);
                                        setIsDepositing(false);
                                      }
                                    }}
                                    style={{ flex: 1, padding: '8px', backgroundColor: '#faad14', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
                                  >
                                    Register &amp; Deposit
                                  </button>
                                  <button
                                    onClick={() => setShowPasskeyRegisterPrompt(false)}
                                    style={{ padding: '8px 14px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* ── In-progress indicator ──────────────────────── */}
                            {isDepositing && depositStep && (
                              <div style={{ marginTop: '14px', padding: '14px', backgroundColor: '#f0f7ff', border: '1px solid #91caff', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '18px', height: '18px', border: '3px solid #4a9eff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                <span style={{ fontSize: '13px', color: '#003a8c', fontWeight: 500 }}>{depositStep}</span>
                              </div>
                            )}

                            {/* ── Success confirmation ───────────────────────── */}
                            {depositSuccess && (
                              <div style={{ marginTop: '14px', padding: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '8px', textAlign: 'center' }}>
                                <div style={{ fontSize: '28px', marginBottom: '6px' }}>✅</div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color: '#135200', marginBottom: '4px' }}>
                                  {depositSuccess.amount} XLM deposited!
                                </div>
                                <div style={{ fontSize: '12px', color: '#237804', marginBottom: depositSuccess.hash ? '6px' : '12px' }}>
                                  Successfully added to the {countryIso2} vault.
                                </div>
                                {depositSuccess.hash && (
                                  <div style={{ fontSize: '11px', marginBottom: '12px' }}>
                                    <a
                                      href={`https://stellar.expert/explorer/testnet/tx/${depositSuccess.hash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#1677ff', textDecoration: 'none' }}
                                    >
                                      View transaction ↗
                                    </a>
                                  </div>
                                )}
                                {vaultBalance !== null && (
                                  <div style={{ fontSize: '12px', color: '#389e0d', marginBottom: '12px', fontWeight: 500 }}>
                                    Your vault balance: {(((vaultBalance ?? 0) + (smartAccountVaultBalance ?? 0)) / 10_000_000).toFixed(2)} XLM
                                    {smartAccountVaultBalance !== null && (vaultBalance ?? 0) > 0 && (smartAccountVaultBalance ?? 0) > 0 && (
                                      <span style={{ color: '#888', fontWeight: 400 }}>
                                        {' '}(Wallet: {((vaultBalance ?? 0) / 10_000_000).toFixed(2)} · Passkey: {((smartAccountVaultBalance ?? 0) / 10_000_000).toFixed(2)})
                                      </span>
                                    )}
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    setDepositSuccess(null);
                                    setDepositAmount('');
                                    setDepositStep(null);
                                    setShowDepositForm(false);
                                  }}
                                  style={{ padding: '8px 24px', backgroundColor: '#52c41a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
                                >
                                  Done
                                </button>
                              </div>
                            )}
                          </div>
                        ) : null}
                        {showTransferForm && (
                          <div style={{
                            padding: '16px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            marginTop: '12px'
                          }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>Transfer from {countryIso2} Vault</h4>
                            {totalVaultBalance !== null && (
                              <div style={{ fontSize: '12px', color: '#4CAF50', marginBottom: '8px', fontWeight: '500' }}>
                                Total Vault Balance: {(totalVaultBalance / 10000000).toFixed(7)} XLM
                              </div>
                            )}

                            {/* Transfer From toggle — shown when passkey account is linked */}
                            {passkeyKitAvailable && smartAccountVaultBalance !== null && (
                              <div style={{ marginBottom: '10px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '5px', fontWeight: 600, textTransform: 'uppercase' }}>Transfer From</div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {[
                                    { label: `👛 Wallet (${((vaultBalance ?? 0) / 1e7).toFixed(2)} XLM)`, val: true },
                                    { label: `🔑 Passkey (${((smartAccountVaultBalance ?? 0) / 1e7).toFixed(2)} XLM)`, val: false },
                                  ].map(({ label, val }) => (
                                    <button key={String(val)} type="button"
                                      onClick={() => setUseWalletTransfer(val)}
                                      disabled={isTransferring}
                                      style={{ flex: 1, padding: '6px 10px', fontSize: '12px', borderRadius: '6px',
                                        border: useWalletTransfer === val ? '1.5px solid #007bff' : '1.5px solid #ddd',
                                        background: useWalletTransfer === val ? '#e8f4ff' : '#fafafa',
                                        color: useWalletTransfer === val ? '#007bff' : '#666',
                                        cursor: isTransferring ? 'not-allowed' : 'pointer', fontWeight: useWalletTransfer === val ? 600 : 400,
                                      }}
                                    >{label}</button>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                                To Country:
                              </label>
                              <select
                                value={transferToCountry}
                                onChange={(e) => {
                                  setTransferToCountry(e.target.value);
                                  setTransferError(null);
                                }}
                                disabled={isTransferring || isLoadingTransferCountries}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontSize: '14px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              >
                                <option value="">
                                  {isLoadingTransferCountries ? 'Checking transfer policies…' : 'Select a country...'}
                                </option>
                                {availableCountries.map((country) => (
                                  <option key={country.iso2} value={country.iso2}>
                                    {country.name} ({country.iso2})
                                  </option>
                                ))}
                                {!isLoadingTransferCountries && availableCountries.length === 0 && (
                                  <option value="" disabled>No countries available for transfer</option>
                                )}
                              </select>
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                                Amount (XLM):
                              </label>
                              <input
                                type="number"
                                step="0.0000001"
                                min="0"
                                max={(() => {
                                  const bal = useWalletTransfer ? (vaultBalance ?? 0) : (smartAccountVaultBalance ?? 0);
                                  return bal > 0 ? (bal / 10000000).toFixed(7) : undefined;
                                })()}
                                value={transferAmount}
                                onChange={(e) => {
                                  setTransferAmount(e.target.value);
                                  setTransferError(null);
                                }}
                                placeholder="10.5"
                                disabled={isTransferring}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontSize: '14px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                            </div>
                            {transferAmount && feeConfig && parseFloat(transferAmount) > 0 && (
                              <div style={{ 
                                marginBottom: '12px', 
                                padding: '8px', 
                                backgroundColor: '#e3f2fd', 
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Transfer Details:</strong>
                                </div>
                                <div>Amount: {parseFloat(transferAmount).toFixed(7)} XLM</div>
                                {feeConfig.feeBps > 0 ? (
                                  <>
                                    <div>Fee ({(feeConfig.feeBps / 100).toFixed(2)}%): {(parseFloat(transferAmount) * feeConfig.feeBps / 10000).toFixed(7)} XLM</div>
                                    <div>You'll receive: <strong>{(parseFloat(transferAmount) * (1 - feeConfig.feeBps / 10000)).toFixed(7)} XLM</strong></div>
                                    <div style={{ marginTop: '3px', fontSize: '11px', color: '#555' }}>
                                      {parseFloat((feeConfig.countryAdminShareBps / 100).toFixed(2))}% of fee → country admin · {parseFloat(((10000 - feeConfig.countryAdminShareBps) / 100).toFixed(2))}% → protocol
                                    </div>
                                  </>
                                ) : (
                                  <div style={{ color: '#4caf50' }}>No fees</div>
                                )}
                              </div>
                            )}
                            {transferError && (
                              <div style={{ 
                                padding: '8px', 
                                backgroundColor: '#fee', 
                                color: '#c33', 
                                borderRadius: '4px', 
                                marginBottom: '12px',
                                fontSize: '12px'
                              }}>
                                {transferError}
                              </div>
                            )}
                            {transferStep && !transferSuccess && (
                              <div style={{ padding: '8px', backgroundColor: '#e8f4fd', color: '#1d6fa5', borderRadius: '4px', marginBottom: '10px', fontSize: '12px' }}>
                                ⏳ {transferStep}
                              </div>
                            )}
                            {transferSuccess && (
                              <div style={{ padding: '12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', marginBottom: '10px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#155724', marginBottom: '4px' }}>✅ Transfer successful!</div>
                                {transferSuccess.hash && (
                                  <a href={`https://stellar.expert/explorer/testnet/tx/${transferSuccess.hash}`}
                                    target="_blank" rel="noopener noreferrer"
                                    style={{ fontSize: '11px', color: '#1677ff', textDecoration: 'none' }}>
                                    View transaction ↗
                                  </a>
                                )}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={async () => {
                                  if (!transferAmount || parseFloat(transferAmount) <= 0) {
                                    setTransferError('Please enter a valid amount');
                                    return;
                                  }
                                  if (!transferToCountry || transferToCountry.length !== 2) {
                                    setTransferError('Please select a destination country');
                                    return;
                                  }
                                  if (transferToCountry.toUpperCase() === countryIso2?.toUpperCase()) {
                                    setTransferError('Cannot transfer to the same country');
                                    return;
                                  }
                                  if (!walletAddress || !countryIso2) {
                                    setTransferError('Wallet or country code missing');
                                    return;
                                  }

                                  const amountInStroops = Math.round(parseFloat(transferAmount) * 10_000_000);
                                  const availableBal = useWalletTransfer
                                    ? (vaultBalance ?? 0)
                                    : (smartAccountVaultBalance ?? 0);

                                  if (amountInStroops > availableBal || availableBal === 0) {
                                    const otherBal = useWalletTransfer ? (smartAccountVaultBalance ?? 0) : (vaultBalance ?? 0);
                                    setTransferError(
                                      `Insufficient ${useWalletTransfer ? 'wallet' : 'passkey'} vault balance. ` +
                                      `Available: ${(availableBal / 1e7).toFixed(7)} XLM.` +
                                      (otherBal >= amountInStroops
                                        ? ` Your ${useWalletTransfer ? '🔑 Passkey' : '👛 Wallet'} account has sufficient funds — switch above.`
                                        : '')
                                    );
                                    return;
                                  }

                                  setIsTransferring(true);
                                  setTransferError(null);
                                  setTransferStep(null);
                                  setTransferSuccess(null);

                                  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                  const assetAddress = process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS || 'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';
                                  const fromIso2 = (countryIso2 || '').toUpperCase();
                                  const toIso2   = transferToCountry.toUpperCase();

                                  try {
                                    if (useWalletTransfer || !passkeyKitAvailable) {
                                      // ── Wallet transfer ────────────────────────────────────
                                      if (!wallet) throw new Error('Wallet not connected');
                                      setTransferStep('Building transaction...');
                                      const { Contract, rpc: SorobanRpc, Networks, TransactionBuilder, BASE_FEE, Address, xdr: xdrSdk } = await import('@stellar/stellar-sdk');
                                      const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
                                      const networkPassphrase = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? Networks.PUBLIC : Networks.TESTNET;
                                      const sorobanServer = new SorobanRpc.Server(rpcUrl);
                                      const publicKey = await wallet.getPublicKey();
                                      const account = await sorobanServer.getAccount(publicKey);
                                      const vaultId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!vaultId) throw new Error('REACT_APP_V2_VAULT_ID not configured');
                                      const contract = new Contract(vaultId);
                                      const fromNum = iso2ToNumeric(fromIso2);
                                      const toNum   = iso2ToNumeric(toIso2);
                                      const amountScVal = xdrSdk.ScVal.scvI128(new xdrSdk.Int128Parts({
                                        hi: xdrSdk.Int64.fromString('0'),
                                        lo: xdrSdk.Uint64.fromString(String(amountInStroops)),
                                      }));
                                      const op = contract.call('transfer_between_countries',
                                        Address.fromString(publicKey).toScVal(),
                                        xdrSdk.ScVal.scvU32(fromNum),
                                        xdrSdk.ScVal.scvU32(toNum),
                                        Address.fromString(assetAddress).toScVal(),
                                        amountScVal,
                                      );
                                      const tx = new TransactionBuilder(account, { fee: BASE_FEE, networkPassphrase }).addOperation(op).setTimeout(60).build();
                                      setTransferStep('Simulating...');
                                      const simResult = await sorobanServer.simulateTransaction(tx);
                                      const { rpc: rpcNs } = await import('@stellar/stellar-sdk');
                                      if (rpcNs.Api.isSimulationError(simResult)) throw new Error(`Simulation failed: ${(simResult as any).error}`);
                                      const assembled = rpcNs.assembleTransaction(tx, simResult).build();
                                      setTransferStep('Waiting for wallet signature...');
                                      const signedXdr = await wallet.signTransaction(assembled.toXDR(), networkPassphrase);
                                      setTransferStep('Submitting to blockchain...');
                                      const resp = await fetch(`${backendUrl}/api/v2/vault/transfer`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ userAddress: publicKey, fromCountry: fromIso2, toCountry: toIso2, assetAddress, amount: amountInStroops, signedXdr }),
                                      });
                                      const data = await resp.json();
                                      if (!data.success) throw new Error(data.error || 'Transfer failed');
                                      setTransferSuccess({ hash: data.hash });
                                      setTransferAmount('');
                                      setTransferToCountry('');
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    } else {
                                      // ── Passkey transfer ───────────────────────────────────
                                      const pkAccounts = passkeyKitService.getAccounts().filter((a: any) => a.walletAddress === walletAddress);
                                      const smartAccount = pkAccounts[selectedPasskeyIdx] ?? pkAccounts[0];
                                      if (!smartAccount) throw new Error('No passkey account found');
                                      const vaultId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!vaultId) throw new Error('REACT_APP_V2_VAULT_ID not configured');
                                      const fromNum = iso2ToNumeric(fromIso2);
                                      const toNum   = iso2ToNumeric(toIso2);

                                      setTransferStep('Preparing transfer...');
                                      const prepResp = await fetch(`${backendUrl}/api/passkey-kit/prepare-transfer`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ smartAccountAddress: smartAccount.accountAddress, fromCountryNumeric: fromNum, toCountryNumeric: toNum, assetAddress, amountStroops: amountInStroops, vaultId }),
                                      });
                                      const prepData = await prepResp.json();
                                      if (!prepData.success) throw new Error(prepData.error || 'Failed to prepare transfer');

                                      setTransferStep('Touch your fingerprint or Face ID...');
                                      const signedXdr = await passkeyKitService.signUnsignedXdr(prepData.unsignedXdr, smartAccount, prepData.latestLedger);

                                      setTransferStep('Submitting to blockchain...');
                                      const submitResp = await fetch(`${backendUrl}/api/passkey-kit/fee-pay-and-submit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
                                      });
                                      const submitData = await submitResp.json();
                                      if (!submitData.success) throw new Error(submitData.error || 'Transfer submission failed');
                                      setTransferSuccess({ hash: submitData.hash });
                                      setTransferAmount('');
                                      setTransferToCountry('');
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    }
                                  } catch (error: any) {
                                    setTransferError(error.message || 'Transfer failed. Please try again.');
                                  } finally {
                                    setIsTransferring(false);
                                    setTransferStep(null);
                                  }
                                }}
                                disabled={isTransferring || !transferAmount || !transferToCountry || !!transferSuccess}
                                style={{
                                  flex: 1,
                                  padding: '8px 16px',
                                  backgroundColor: isTransferring ? '#ccc' : '#007bff',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: (isTransferring || !!transferSuccess) ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {isTransferring ? 'Transferring...' : 'Confirm Transfer'}
                              </button>
                              <button
                                onClick={() => {
                                  setShowTransferForm(false);
                                  setTransferAmount('');
                                  setTransferToCountry('');
                                  setTransferError(null);
                                  setTransferStep(null);
                                  setTransferSuccess(null);
                                }}
                                disabled={isTransferring}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#666',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: isTransferring ? 'not-allowed' : 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                {transferSuccess ? 'Done' : 'Cancel'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ── Withdraw Form ────────────────────────────────── */}
                        {showWithdrawForm && (
                          <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                              Withdraw from {countryIso2} Vault
                              <ExpertLink id={process.env.REACT_APP_V2_VAULT_ID} />
                            </h4>

                            {/* Balances */}
                            {(() => {
                              const walletBal = vaultBalance ?? 0;
                              const smartBal  = smartAccountVaultBalance ?? 0;
                              return (
                                <div style={{ marginBottom: '10px', fontSize: '12px', color: '#555' }}>
                                  {walletBal > 0 && <div>Wallet vault balance: <strong>{(walletBal / 1e7).toFixed(4)} XLM</strong></div>}
                                  {smartBal > 0  && <div>Passkey vault balance: <strong>{(smartBal / 1e7).toFixed(4)} XLM</strong></div>}
                                </div>
                              );
                            })()}

                            {/* Auth Method — show whenever passkey is available and linked account exists */}
                            {passkeyKitAvailable && smartAccountVaultBalance !== null && (
                              <div style={{ marginBottom: '10px' }}>
                                <div style={{ fontSize: '11px', color: '#888', marginBottom: '5px', fontWeight: 600, textTransform: 'uppercase' }}>Withdraw From</div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  {[
                                    { label: `👛 Wallet (${((vaultBalance ?? 0) / 1e7).toFixed(2)} XLM)`, val: true },
                                    { label: `🔑 Passkey (${((smartAccountVaultBalance ?? 0) / 1e7).toFixed(2)} XLM)`, val: false },
                                  ].map(({ label, val }) => (
                                    <button key={String(val)} type="button"
                                      onClick={() => setUseWalletWithdraw(val)}
                                      disabled={isWithdrawing}
                                      style={{ flex: 1, padding: '6px 10px', fontSize: '12px', borderRadius: '6px',
                                        border: useWalletWithdraw === val ? '1.5px solid #dc3545' : '1.5px solid #ddd',
                                        background: useWalletWithdraw === val ? '#fff5f5' : '#fafafa',
                                        color: useWalletWithdraw === val ? '#dc3545' : '#666',
                                        cursor: isWithdrawing ? 'not-allowed' : 'pointer', fontWeight: useWalletWithdraw === val ? 600 : 400,
                                      }}
                                    >{label}</button>
                                  ))}
                                </div>
                                {/* Passkey account selector */}
                                {!useWalletWithdraw && (() => {
                                  const pkAccounts = passkeyKitService.getAccounts().filter(a => a.walletAddress === walletAddress);
                                  if (pkAccounts.length <= 1) return null;
                                  return (
                                    <div style={{ marginTop: '8px' }}>
                                      <select value={selectedPasskeyIdx} onChange={e => setSelectedPasskeyIdx(Number(e.target.value))}
                                        disabled={isWithdrawing}
                                        style={{ width: '100%', padding: '6px', fontSize: '12px', borderRadius: '4px', border: '1px solid #ddd' }}>
                                        {pkAccounts.map((acc, idx) => (
                                          <option key={acc.credentialId} value={idx}>{acc.accountAddress.slice(0,8)}…{acc.accountAddress.slice(-6)}</option>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Amount */}
                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', fontWeight: 600 }}>Amount (XLM)</label>
                              <input type="number" min="0.0000001" step="1" value={withdrawAmount}
                                onChange={e => setWithdrawAmount(e.target.value)}
                                disabled={isWithdrawing}
                                placeholder="Enter amount"
                                style={{ width: '100%', padding: '8px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                              />
                            </div>

                            {withdrawError && <div style={{ padding: '8px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px', marginBottom: '10px', fontSize: '12px' }}>{withdrawError}</div>}
                            {withdrawStep && !withdrawSuccess && <div style={{ padding: '8px', backgroundColor: '#e8f4fd', color: '#1d6fa5', borderRadius: '4px', marginBottom: '10px', fontSize: '12px' }}>⏳ {withdrawStep}</div>}

                            {/* Success */}
                            {withdrawSuccess && (
                              <div style={{ padding: '12px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', marginBottom: '10px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#155724', marginBottom: '4px' }}>✅ Withdrawal successful!</div>
                                <div style={{ fontSize: '12px', color: '#155724' }}>{withdrawSuccess.amount} XLM returned to your account</div>
                                {withdrawSuccess.hash && <div style={{ fontSize: '10px', color: '#777', marginTop: '4px', wordBreak: 'break-all' }}>TX: {withdrawSuccess.hash}</div>}
                              </div>
                            )}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                              <button type="button"
                                disabled={isWithdrawing || !withdrawAmount || !!withdrawSuccess}
                                onClick={async () => {
                                  const amount = withdrawAmount.trim();
                                  if (!amount || parseFloat(amount) <= 0) return;
                                  setIsWithdrawing(true);
                                  setWithdrawError(null);
                                  setWithdrawStep(null);
                                  setWithdrawSuccess(null);

                                  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                  const amountStroops = Math.round(parseFloat(amount) * 1e7);
                                  const assetAddress = process.env.REACT_APP_NATIVE_XLM_SAC_ADDRESS || 'CDLZFC3SYJYDZT7K67VZ75HPJVIEUVNIXF47ZG2FB2RMQQVU2HHGCYSC';

                                  // Pre-flight balance check — catch the mismatch before hitting the chain
                                  const availableBal = useWalletWithdraw
                                    ? (vaultBalance ?? 0)
                                    : (smartAccountVaultBalance ?? 0);
                                  if (amountStroops > availableBal) {
                                    setWithdrawError(
                                      `Insufficient ${useWalletWithdraw ? 'wallet' : 'passkey'} vault balance. ` +
                                      `Available: ${(availableBal / 1e7).toFixed(7)} XLM, ` +
                                      `Requested: ${amount} XLM.` +
                                      ((!useWalletWithdraw === false && (smartAccountVaultBalance ?? 0) >= amountStroops)
                                        ? ' Your passkey account has sufficient funds — select 🔑 Passkey above.' : '')
                                    );
                                    setIsWithdrawing(false);
                                    return;
                                  }

                                  try {
                                    if (useWalletWithdraw || !passkeyKitAvailable) {
                                      // ── Wallet withdraw ───────────────────────────────────
                                      if (!wallet) throw new Error('Wallet not connected');
                                      setWithdrawStep('Building transaction...');
                                      const { Contract, rpc: SorobanRpc, Networks, TransactionBuilder, BASE_FEE, Address, xdr: xdrSdk } = await import('@stellar/stellar-sdk');
                                      const rpcUrl = process.env.REACT_APP_SOROBAN_RPC_URL || 'https://soroban-testnet.stellar.org';
                                      const networkPassphrase = process.env.REACT_APP_STELLAR_NETWORK === 'mainnet' ? Networks.PUBLIC : Networks.TESTNET;
                                      const sorobanServer = new SorobanRpc.Server(rpcUrl);
                                      const publicKey = await wallet.getPublicKey();
                                      const account = await sorobanServer.getAccount(publicKey);
                                      const vaultId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!vaultId) throw new Error('REACT_APP_V2_VAULT_ID not configured');
                                      const contract = new Contract(vaultId);

                                      const numericCountry = iso2ToNumeric((countryIso2 || '').toUpperCase()) || countryCode;
                                      const amountScVal = xdrSdk.ScVal.scvI128(new xdrSdk.Int128Parts({
                                        hi: xdrSdk.Int64.fromString('0'),
                                        lo: xdrSdk.Uint64.fromString(String(amountStroops)),
                                      }));

                                      const op = contract.call('withdraw',
                                        Address.fromString(publicKey).toScVal(),
                                        xdrSdk.ScVal.scvU32(numericCountry),
                                        Address.fromString(assetAddress).toScVal(),
                                        amountScVal,
                                        xdrSdk.ScVal.scvVoid(),
                                        xdrSdk.ScVal.scvVoid(),
                                      );
                                      const tx = new TransactionBuilder(account, { fee: BASE_FEE, networkPassphrase }).addOperation(op).setTimeout(60).build();
                                      setWithdrawStep('Simulating...');
                                      const simResult = await sorobanServer.simulateTransaction(tx);
                                      const { rpc: rpcNs } = await import('@stellar/stellar-sdk');
                                      if (rpcNs.Api.isSimulationError(simResult)) throw new Error(`Simulation failed: ${(simResult as any).error}`);
                                      const assembled = rpcNs.assembleTransaction(tx, simResult).build();
                                      setWithdrawStep('Waiting for wallet signature...');
                                      const signedXdr = await wallet.signTransaction(assembled.toXDR(), networkPassphrase);
                                      setWithdrawStep('Submitting to blockchain...');
                                      const resp = await fetch(`${backendUrl}/api/smart-wallet/withdraw`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ userAddress: publicKey, countryCode: (countryIso2 || '').toUpperCase(), assetAddress, amount: amountStroops, signedXdr }),
                                      });
                                      const data = await resp.json();
                                      if (!data.success) throw new Error(data.error || 'Withdrawal failed');
                                      setWithdrawSuccess({ amount, hash: data.hash });
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    } else {
                                      // ── Passkey withdraw ──────────────────────────────────
                                      const pkAccounts = passkeyKitService.getAccounts().filter(a => a.walletAddress === walletAddress);
                                      const smartAccount = pkAccounts[selectedPasskeyIdx] ?? pkAccounts[0];
                                      if (!smartAccount) throw new Error('No passkey account found');
                                      const vaultId = process.env.REACT_APP_V2_VAULT_ID;
                                      if (!vaultId) throw new Error('REACT_APP_V2_VAULT_ID not configured');

                                      const numericCountry = iso2ToNumeric((countryIso2 || '').toUpperCase()) || countryCode;

                                      setWithdrawStep('Preparing withdrawal...');
                                      const prepResp = await fetch(`${backendUrl}/api/passkey-kit/prepare-withdraw`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ smartAccountAddress: smartAccount.accountAddress, countryNumeric: numericCountry, assetAddress, amountStroops, vaultId }),
                                      });
                                      const prepData = await prepResp.json();
                                      if (!prepData.success) throw new Error(prepData.error || 'Failed to prepare withdrawal');

                                      setWithdrawStep('Touch your fingerprint or Face ID...');
                                      const signedXdr = await passkeyKitService.signUnsignedXdr(prepData.unsignedXdr, smartAccount, prepData.latestLedger);

                                      setWithdrawStep('Submitting to blockchain...');
                                      const submitResp = await fetch(`${backendUrl}/api/passkey-kit/fee-pay-and-submit`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ xdrWithPasskeySig: signedXdr }),
                                      });
                                      const submitData = await submitResp.json();
                                      if (!submitData.success) throw new Error(submitData.error || 'Submission failed');

                                      setWithdrawSuccess({ amount, hash: submitData.hash });
                                      await loadCountryVaultInfo();
                                      onDepositSuccess?.();
                                    }
                                  } catch (err: any) {
                                    setWithdrawError(err.message || 'Withdrawal failed');
                                    setWithdrawStep(null);
                                  } finally {
                                    setIsWithdrawing(false);
                                  }
                                }}
                                style={{ flex: 1, padding: '8px 16px', backgroundColor: isWithdrawing || !!withdrawSuccess ? '#ccc' : '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: isWithdrawing || !!withdrawSuccess ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                              >
                                {isWithdrawing ? 'Withdrawing...' : 'Confirm Withdraw'}
                              </button>
                              <button type="button" onClick={() => { setShowWithdrawForm(false); setWithdrawAmount(''); setWithdrawError(null); setWithdrawStep(null); setWithdrawSuccess(null); }}
                                disabled={isWithdrawing}
                                style={{ padding: '8px 16px', backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '4px', cursor: isWithdrawing ? 'not-allowed' : 'pointer', fontSize: '14px' }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {!walletAddress && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
                    Connect wallet to see your balance and make deposits
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>
                  Country vault not registered
                </div>
                {/* Still show balance even for unregistered vaults — funds can arrive via transfer */}
                {walletAddress && !isLoadingVaultBalance && (() => {
                  const walletBal = vaultBalance ?? 0;
                  const smartBal  = smartAccountVaultBalance ?? 0;
                  const combined  = walletBal + smartBal;
                  if (combined <= 0) return null;
                  return (
                    <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #ddd' }}>
                      <div style={{ fontSize: '12px', fontWeight: 700, color: '#555', marginBottom: '4px', textTransform: 'uppercase' }}>
                        Your Vault Balance
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#FFD700' }}>
                        {(combined / 10_000_000).toFixed(7)} XLM
                      </div>
                      {smartAccountVaultBalance !== null && (
                        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '3px', lineHeight: '1.6' }}>
                          {walletBal > 0 && <span>Wallet: {(walletBal / 10_000_000).toFixed(2)} XLM{smartBal > 0 ? '  ·  ' : ''}</span>}
                          {smartBal > 0  && <span>Passkey: {(smartBal / 10_000_000).toFixed(2)} XLM</span>}
                        </div>
                      )}
                      <div style={{ marginTop: '8px', fontSize: '11px', color: '#e67e22', backgroundColor: '#fef9e7', padding: '6px 8px', borderRadius: '4px' }}>
                        ⚠ This country's vault has not been officially registered yet. Contact the admin to enable withdrawals.
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Admin Actions */}
        {(isMainAdmin || isCountryAdmin) && onManageCountry && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #ddd' }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Admin Actions</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onManageCountry({ code: countryCode, name: countryName });
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Manage Country
              </button>
              {contractClient && (
                <button
                  onClick={async () => {
                    if (!contractClient) return;
                    const newAllowed = !isAllowed;
                    try {
                      await contractClient.setCountryAllowed(countryCode, newAllowed);
                      setIsAllowed(newAllowed);
                      if (onCountryAllowedChange) onCountryAllowedChange(countryCode, newAllowed);

                      // Refresh vault info — register_country may have just created the record.
                      // Bust the cache first so we don't get the stale null that was stored
                      // when the country wasn't registered yet.
                      if (countryIso2) {
                        readOnlyClient.clearCountryInfoCache(countryIso2);
                        setIsLoadingVault(true);
                        try {
                          const info = await readOnlyClient.getCountryInfo(countryIso2.toUpperCase());
                          setVaultInfo(info);
                          // Also reload the V2 policy so the deposit buttons gate correctly
                          if (countryCode) {
                            readOnlyClient.getFullCountryPolicy(countryCode).then(p => {
                              setFullPolicy(p ? {
                                deposits_enabled: p.deposits_enabled,
                                withdrawals_enabled: p.withdrawals_enabled,
                                transfers_in_enabled: p.transfers_in_enabled,
                                transfers_out_enabled: p.transfers_out_enabled,
                                restricted: p.restricted,
                                require_webauthn_withdraw: p.require_webauthn_withdraw,
                              } : null);
                            }).catch(() => {});
                          }
                        } catch {
                          setVaultInfo(null);
                        } finally {
                          setIsLoadingVault(false);
                        }
                      }
                    } catch (error: any) {
                      alert(`Failed to update policy: ${error.message}`);
                    }
                  }}
                  disabled={isLoadingPolicy}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: isAllowed ? '#d32f2f' : '#4caf50',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoadingPolicy ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: isLoadingPolicy ? 0.6 : 1,
                  }}
                >
                  {isLoadingPolicy ? 'Updating...' : (isAllowed ? 'Deny Country' : 'Allow Country')}
                </button>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
              {isMainAdmin ? 'You are the main admin.' : 'You are the country admin for this country.'}
            </div>
          </div>
        )}

        {!(isMainAdmin || isCountryAdmin) && (
          <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            This is a public profile. Only admins can modify country settings.
          </div>
        )}
      </div>
      
      {/* KYC Submission Modal */}
      {countryIso2 && (
        <KYCSubmissionModal
          isOpen={showKYCModal}
          onClose={() => setShowKYCModal(false)}
          countryCode={countryIso2}
          countryName={countryName}
          contractClient={contractClient}
          readOnlyClient={readOnlyClient}
          walletAddress={walletAddress}
          onKYCSubmitted={async () => {
            // Reload KYC status after submission
            if (walletAddress && countryIso2) {
              setIsLoadingKYC(true);
              try {
                const kycData = await readOnlyClient.getKYCData(walletAddress, countryIso2);
                if (kycData) {
                  setKycStatus(kycData.kycStatus as 'pending' | 'approved' | 'rejected');
                } else {
                  setKycStatus(null);
                }
              } catch (error) {
              } finally {
                setIsLoadingKYC(false);
              }
            }
          }}
        />
      )}
    </div>
  );
};
